import { FC, ReactNode, useMemo } from 'react';
import {
    ListItem,
    ListItemText,
    IconButton,
    Stack,
    Chip,
    Typography,
    Box,
    TypographyProps,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIndicatorIcon,
    Sell as TagIcon,
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ScheduleItem, Category, EventData } from '@/types';
import { useI18nContext } from '@/i18n/i18n-react';
import { replaceCategoryPlaceholders } from '@/utils/schedulePlaceholders';
import { splitTags } from '@/utils/tags';

interface HighlightableTextProps extends Omit<TypographyProps, 'onClick'> {
    highlighted: boolean;
    onClick: () => void;
    children: ReactNode;
}

const HighlightableText: FC<HighlightableTextProps> = ({
    highlighted,
    onClick,
    children,
    sx,
    ...props
}) => (
    <Typography
        onClick={onClick}
        sx={{
            cursor: 'pointer',
            backgroundColor: highlighted ? 'warning.main' : 'transparent',
            color: highlighted ? 'warning.contrastText' : 'inherit',
            padding: highlighted ? '2px 4px' : 0,
            borderRadius: highlighted ? '2px' : 0,
            display: 'inline-block',
            transition: 'background-color 0.2s, color 0.2s',
            '&:hover': {
                backgroundColor: highlighted ? 'warning.dark' : 'action.hover',
            },
            ...sx,
        }}
        {...props}
    >
        {children}
    </Typography>
);

interface ScheduleItemRowProps {
    item: ScheduleItem & { displayStartTime: string; displayEndTime: string };
    canEdit: boolean;
    reorderEnabled: boolean;
    editItemId: number | null;
    isDeleting: boolean;
    categories: Category[];
    eventData?: EventData;
    onEdit: (item: ScheduleItem) => void;
    onDelete: (id: number) => void;
    onToggleHighlight: (itemId: number, field: 'title' | 'responsible' | 'note') => void;
    formatTime: (time?: string) => string;
}

const ScheduleItemRow: FC<ScheduleItemRowProps> = ({
    item,
    canEdit,
    reorderEnabled,
    editItemId,
    isDeleting,
    categories,
    eventData,
    onEdit,
    onDelete,
    onToggleHighlight,
    formatTime,
}) => {
    const { LL } = useI18nContext();

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item.id!,
        disabled: !canEdit || !reorderEnabled,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const tags = useMemo(() => splitTags(item.tags), [item.tags]);

    return (
        <ListItem
            ref={setNodeRef}
            style={style}
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: { xs: 0.5, sm: 1 },
                backgroundColor: editItemId === item.id ? 'action.selected' : 'background.paper',
                cursor: 'default',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                py: 1,
                px: { xs: 1, sm: 2 },
            }}
        >
            {canEdit && reorderEnabled && (
                <Box
                    {...attributes}
                    {...listeners}
                    sx={{
                        mr: { xs: 0.5, sm: 1 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        touchAction: 'none',
                        cursor: 'grab',
                        minWidth: { xs: 32, sm: 24 },
                        minHeight: { xs: 32, sm: 24 },
                        ml: -0.5,
                    }}
                >
                    <DragIndicatorIcon sx={{ fontSize: { xs: 28, sm: 20 } }} />
                </Box>
            )}
            <ListItemText
                slotProps={{ secondary: { component: 'div' } }}
                primary={
                    <Stack spacing={0.5} sx={{ alignItems: 'start' }}>
                        <Stack
                            direction="row"
                            spacing={{ xs: 0.5, sm: 1 }}
                            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                            useFlexGap={true}
                        >
                            {tags.map((tag) => (
                                <Chip
                                    key={tag}
                                    label={tag}
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    icon={<TagIcon />}
                                    sx={{
                                        height: 22,
                                        '& .MuiChip-label': { fontWeight: 600 },
                                    }}
                                />
                            ))}
                            <HighlightableText
                                variant="subtitle1"
                                highlighted={!!item.highlightTitle}
                                onClick={() => onToggleHighlight(item.id!, 'title')}
                                sx={{
                                    fontWeight: item.highlightTitle ? 'bold' : 600,
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                }}
                            >
                                {item.title}
                            </HighlightableText>
                        </Stack>

                        {item.responsible && (
                            <HighlightableText
                                variant="body2"
                                highlighted={!!item.highlightResponsible}
                                onClick={() => onToggleHighlight(item.id!, 'responsible')}
                                sx={{
                                    mb: 0.5,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                }}
                            >
                                {LL.scheduleModal.responsibleLabel()}:{' '}
                                {replaceCategoryPlaceholders(
                                    item.responsible || '',
                                    categories,
                                    eventData
                                )}
                            </HighlightableText>
                        )}

                        {item.note && (
                            <HighlightableText
                                variant="body2"
                                highlighted={!!item.highlightNote}
                                onClick={() => onToggleHighlight(item.id!, 'note')}
                                sx={{
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    color: item.highlightNote
                                        ? 'warning.contrastText'
                                        : 'text.secondary',
                                }}
                            >
                                {item.note}
                            </HighlightableText>
                        )}
                    </Stack>
                }
                secondary={
                    <Stack
                        direction="row"
                        spacing={{ xs: 0.5, sm: 1 }}
                        sx={{
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 0.5,
                        }}
                    >
                        {item.displayStartTime && (
                            <Chip
                                label={`${formatTime(item.displayStartTime)} - ${formatTime(item.displayEndTime)}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {item.duration && (
                            <Chip
                                label={`${item.duration} ${LL.scheduleModal.minutesShort()}`}
                                size="small"
                                variant="outlined"
                            />
                        )}
                        <Box sx={{ flex: 1 }} />
                        {canEdit && !reorderEnabled ? (
                            <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }}>
                                <IconButton
                                    edge="end"
                                    onClick={() => onEdit(item)}
                                    disabled={isDeleting}
                                    size="small"
                                    sx={{ p: { xs: 0.5, sm: 1 } }}
                                >
                                    <EditIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                </IconButton>
                                <IconButton
                                    edge="end"
                                    onClick={() => item.id && onDelete(item.id)}
                                    disabled={isDeleting}
                                    color="error"
                                    size="small"
                                    sx={{ p: { xs: 0.5, sm: 1 } }}
                                >
                                    <DeleteIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                </IconButton>
                            </Stack>
                        ) : undefined}
                    </Stack>
                }
            />
        </ListItem>
    );
};

export default ScheduleItemRow;
